import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { useInfoUpdates } from '../../context/InfoUpdatesContext';
import api from '../../services/api';
import { getCloudinaryUrl } from '../../utils/cloudinaryImage';
import { scheduleIdle } from '../../utils/scheduleIdle';
import FireIcon from '../../assets/icons/FireIcon';
import FreshIcon from '../../assets/icons/FreshIcon';
import FeedIcon from '../../assets/icons/FeedIcon';
import MessagesIcon from '../../assets/icons/MessagesIcon';
import StarIcon from '../../assets/icons/StarIcon';
import BookmarkIcon from '../../assets/icons/BookmarkIcon';
import InfoIcon from '../../assets/icons/InfoIcon';
import './Sidebar.css';

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { unreadChatsCount } = useChat() || {};
    const { hasUnreadUpdates } = useInfoUpdates() || {};
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        return scheduleIdle(loadCategories, 1200);
    }, []);

    const loadCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const isActive = (path) => location.pathname === path;

    const getCategoryName = (category) => {
        if (!category.isTranslatable) return category.name;
        return t(`categories.${category.slug}`, { defaultValue: category.name });
    };

    return (
        <div className="sidebar">
            <nav className="sidebar__nav">
                <div className="sidebar__items">
                    <button
                        className={`sidebar__item ${isActive('/') || isActive('/popular') ? 'sidebar__item--active' : ''}`}
                        onClick={() => navigate('/popular')}
                    >
                        <span className="sidebar__icon"><FireIcon /></span>
                        <span className="sidebar__text">{t('sidebar.popular')}</span>
                    </button>

                    <button
                        className={`sidebar__item ${isActive('/new') ? 'sidebar__item--active' : ''}`}
                        onClick={() => navigate('/new')}
                    >
                        <span className="sidebar__icon"><FreshIcon /></span>
                        <span className="sidebar__text">{t('sidebar.new')}</span>
                    </button>

                    <button
                        className={`sidebar__item ${isActive('/feed') ? 'sidebar__item--active' : ''}`}
                        onClick={() => user ? navigate('/feed') : navigate(`${location.pathname}?modal=login`)}
                    >
                        <span className="sidebar__icon"><FeedIcon /></span>
                        <span className="sidebar__text">{t('sidebar.feed')}</span>
                    </button>

                    <button
                        className={`sidebar__item ${isActive('/bookmarks') ? 'sidebar__item--active' : ''}`}
                        onClick={() => user ? navigate('/bookmarks') : navigate(`${location.pathname}?modal=login`)}
                    >
                        <span className="sidebar__icon"><BookmarkIcon /></span>
                        <span className="sidebar__text">{t('sidebar.bookmarks')}</span>
                    </button>

                    <button
                        className={`sidebar__item ${isActive('/messages') ? 'sidebar__item--active' : ''}`}
                        onClick={() => user ? navigate('/messages') : navigate(`${location.pathname}?modal=login`)}
                    >
                        <span className="sidebar__icon"><MessagesIcon /></span>
                        <span className="sidebar__text">{t('sidebar.messages')}</span>
                        {unreadChatsCount > 0 && (
                            <span className="sidebar__badge">{unreadChatsCount}</span>
                        )}
                    </button>

                    <button
                        className={`sidebar__item ${isActive('/rating') ? 'sidebar__item--active' : ''}`}
                        onClick={() => navigate('/rating')}
                    >
                        <span className="sidebar__icon"><StarIcon /></span>
                        <span className="sidebar__text">{t('sidebar.rating')}</span>
                    </button>

                    <button
                        className={`sidebar__item ${location.pathname.startsWith('/info') ? 'sidebar__item--active' : ''}`}
                        onClick={() => navigate('/info')}
                    >
                        <span className="sidebar__icon"><InfoIcon /></span>
                        <span className="sidebar__text">{t('sidebar.info')}</span>
                        {hasUnreadUpdates && <span className="sidebar__dot"></span>}
                    </button>
                </div>

                <div className="sidebar__divider"></div>

                <div className="sidebar__section-title">{t('sidebar.topics')}</div>
                <div className="sidebar__topics">
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            className={`sidebar__item ${location.pathname === `/${category.slug}` ? 'sidebar__item--active' : ''}`}
                            onClick={() => navigate(`/${category.slug}`)}
                        >
                            <img
                                className="sidebar__avatar"
                                src={getCloudinaryUrl(category.imageUrl, { width: 56, height: 56 })}
                                alt=""
                                loading="lazy"
                            />
                            <span className="sidebar__text">{getCategoryName(category)}</span>
                        </button>
                    ))}
                </div>
            </nav>
        </div>
    );
};

export default Sidebar;
